<p-dialog
  [visible]="visible()"
  (visibleChange)="visible.set($event)"
  [modal]="true"
  [draggable]="false"
  [style]="{ width: '42rem', maxWidth: '95vw' }"
  [header]="'booking.chain.dialog.title' | translate"
>
  <p class="mb-3 text-sm text-surface-600">{{ 'booking.chain.dialog.description' | translate }}</p>

  <div class="border-round mb-4 inline-flex items-center gap-2 bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-800">
    <i class="pi pi-calendar"></i>
    @if (tail().flatLabel) {
      {{ 'booking.chain.dialog.anchor' | translate: { date: (tail().endDate | date: 'yyyy-MM-dd'), flat: tail().flatLabel } }}
    } @else {
      {{ 'booking.chain.dialog.anchorNoFlat' | translate: { date: (tail().endDate | date: 'yyyy-MM-dd') } }}
    }
  </div>

  @if (loading()) {
    <p-skeleton height="3rem" styleClass="mb-2"></p-skeleton>
    <p-skeleton height="3rem" styleClass="mb-2"></p-skeleton>
    <p-skeleton height="3rem"></p-skeleton>
  } @else if (loadFailed()) {
    <app-error-state
      [title]="'booking.chain.dialog.loadErrorTitle'"
      [message]="'booking.chain.dialog.loadErrorMessage'"
    ></app-error-state>
  } @else if (!candidates().length) {
    <app-empty-state
      [title]="'booking.chain.dialog.emptyTitle'"
      [description]="'booking.chain.dialog.emptyDescription'"
    ></app-empty-state>
  } @else {
    <p-iconfield iconPosition="left" class="mb-4 block">
      <p-inputicon styleClass="pi pi-search"></p-inputicon>
      <input
        pInputText
        class="w-full"
        [value]="search()"
        (input)="search.set($any($event.target).value)"
        [placeholder]="'booking.chain.dialog.search' | translate"
      />
    </p-iconfield>

    @if (sameFlat().length) {
      <div class="mb-4">
        <div class="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-surface-500">
          <i class="pi pi-check-circle text-green-600"></i>
          {{ 'booking.chain.dialog.sameFlat' | translate: { flat: tail().flatLabel } }}
          <span class="rounded-full bg-surface-100 px-2 text-[10px]">{{ sameFlat().length }}</span>
        </div>
        @for (candidate of sameFlat(); track candidate.id) {
          <ng-container *ngTemplateOutlet="row; context: { $implicit: candidate }"></ng-container>
        }
      </div>
    }

    @if (otherFlat().length) {
      <div class="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-surface-500">
        <i class="pi pi-exclamation-triangle text-amber-600"></i>
        {{ 'booking.chain.dialog.otherFlat' | translate }}
        <span class="rounded-full bg-surface-100 px-2 text-[10px]">{{ otherFlat().length }}</span>
      </div>
      @if (othersExpanded()) {
        @for (candidate of otherFlat(); track candidate.id) {
          <ng-container *ngTemplateOutlet="row; context: { $implicit: candidate }"></ng-container>
        }
      } @else {
        <button
          type="button"
          class="w-full rounded-lg border border-dashed border-surface-300 bg-surface-50 p-3 text-xs text-surface-600 transition-colors duration-200 hover:bg-surface-100"
          (click)="othersExpanded.set(true)"
        >
          {{ 'booking.chain.dialog.otherFlat' | translate }} ({{ otherFlat().length }})
        </button>
      }
    }
  }

  <ng-template #row let-candidate>
    <div
      class="mb-1.5 flex items-center gap-3 rounded-lg border p-3 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      [class]="
        candidate.alreadyChained
          ? 'border-surface-200 bg-surface-50 opacity-60'
          : candidate.id === selectedId()
            ? 'border-2 border-primary bg-primary-50'
            : candidate.sameFlat
              ? 'border-surface-200 bg-surface-0 cursor-pointer hover:border-primary-300'
              : 'border-amber-200 bg-amber-50 cursor-pointer hover:border-amber-400'
      "
      [attr.role]="candidate.alreadyChained ? 'presentation' : 'button'"
      [attr.tabindex]="candidate.alreadyChained ? -1 : 0"
      (click)="onSelect(candidate)"
      (keydown.enter)="onSelect(candidate)"
      (keydown.space)="onSelect(candidate); $event.preventDefault()"
    >
      <div class="min-w-0 flex-1">
        <div class="text-sm font-bold text-surface-900">
          {{ candidate.startDate | date: 'yyyy-MM-dd' }} <i class="pi pi-arrow-right mx-1 text-[10px]"></i>
          {{ candidate.endDate | date: 'yyyy-MM-dd' }}
        </div>
        <div class="mt-0.5 text-xs text-surface-600">{{ candidate.guestName }} · {{ candidate.statusName }} · #{{ candidate.id }}</div>
        @if (candidate.alreadyChained) {
          <div class="mt-1 flex items-center gap-1.5 text-[11px] text-red-700">
            <i class="pi pi-ban"></i>{{ 'booking.chain.dialog.alreadyChained' | translate }}
          </div>
        } @else if (!candidate.flatLabel) {
          <div class="mt-1 flex items-center gap-1.5 text-[11px] text-amber-700">
            <i class="pi pi-exclamation-triangle"></i>{{ 'booking.chain.dialog.noFlat' | translate }}
          </div>
        } @else if (!candidate.sameFlat) {
          <div class="mt-1 flex items-center gap-1.5 text-[11px] text-amber-700">
            <i class="pi pi-exclamation-triangle"></i>{{ 'booking.chain.dialog.otherFlatWarning' | translate }}
          </div>
        }
      </div>
      <span
        class="whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-semibold"
        [class]="candidate.flatLabel ? (candidate.sameFlat ? 'bg-green-100 text-green-800' : 'bg-surface-100 text-surface-600') : 'bg-red-50 italic text-red-800'"
      >
        {{ candidate.flatLabel ?? ('booking.chain.dialog.noFlatChip' | translate) }}
      </span>
      <span class="whitespace-nowrap text-[11px] text-surface-400">
        {{ 'booking.chain.nights' | translate: { count: candidate.nights } }}
      </span>
    </div>
  </ng-template>

  <ng-template pTemplate="footer">
    <div class="flex w-full items-center justify-between gap-4">
      @if (selected(); as candidate) {
        <span class="text-left text-[11px] leading-relaxed text-surface-500">
          {{
            'booking.chain.dialog.preview'
              | translate
                : {
                    start: (chain().links[0].startDate | date: 'yyyy-MM-dd'),
                    end: previewEnd(candidate),
                    nights: previewNights(candidate),
                    count: chain().links.length + 1,
                    target: candidate.id
                  }
          }}
        </span>
      } @else {
        <span></span>
      }
      <div class="flex gap-2">
        <p-button [label]="'common.cancel' | translate" severity="secondary" [text]="true" (onClick)="onCancel()"></p-button>
        <p-button
          [label]="'booking.chain.dialog.confirm' | translate"
          icon="pi pi-link"
          [disabled]="!selected()"
          [loading]="saving()"
          (onClick)="onConfirm()"
        ></p-button>
      </div>
    </div>
  </ng-template>
</p-dialog>
